import React, { useEffect } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, Prompt } from 'react-router';
import { useStatus, useData, combineStatuses } from 'resift';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import { updateClient } from 'store/client';
import getRootNodes from 'fetches/getRootNodes';
import {
  defaultGroupEmailFormValue,
  defaultGroupMessageFormValue,
  defaultOrgChartExportFormValue,
  defaultAssistantFormValue,
  defaultOnboardingFormValue,
} from './constants';
import * as Types from './typings';
import useFormSnackbar from 'components/Form/helpers/useFormSnackbar';

import useEntityTypes, { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';
import makeEntityTypesFetch from 'store/configuration/actions/entityTypesFetch';
import makeUpdateLayoutFetch from 'pages/ProfileLayout/fetches/updateLayoutFetch';
import getDefaultPersonSettings from 'fetches/getDefaultPersonSettings';
import updateDefaultPersonSettings from 'fetches/updateDefaultPersonSettings';
import * as FormValues from 'types/FormValue';

import Page from 'components/Page/newComponents';
import Breadcrumbs from 'components/Page/Breadcrumbs';
import { Formik, Form } from 'formik';
import PageTitle from 'components/Page/PageTitle';
import PageFormToolbar from 'components/Page/newComponents/PageFormToolbar';
import PageFormErrorMessage from 'components/Page/newComponents/PageFormErrorMessage';
import SaveButton from 'components/Page/SaveButton';
import CancelButton from 'components/Page/CancelButton';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Loader from 'components/Loader';
import Directory from './tabs/Directory/Directory';
import Profile from './tabs/Profile/Profile';
import OrgChart from './tabs/OrgChart/OrgChart';
import Onboarding from './tabs/Other/Onboarding';
import HandleRedirect from 'molecules/HandleRedirect';
import convertTimezoneAndWorkHoursValueForRequest from './tabs/Profile/TimezoneAndWorkHours/helpers/convertTimezoneAndWorkHoursValueForRequest';
import getDefaultTimezoneAndWorkHoursFormValue from './tabs/Profile/TimezoneAndWorkHours/helpers/getDefaultTimezoneAndWorkHoursFormValue';

function Features() {
  const dispatch = useDispatch();
  const _client = useSelector((state: any) => state.client);
  const entityTypesFetch = makeEntityTypesFetch();
  const updateLayoutFetch = makeUpdateLayoutFetch();
  const updateLayoutStatus = useStatus(updateLayoutFetch);
  const entityTypes = useEntityTypes();
  const userConfig = _get(entityTypes, PERSON_ENTITY_TYPE_KEY);
  const clientStatus = useSelector((state: any) => state.client.status);
  const defaultPersonSettings = useData(getDefaultPersonSettings);
  const defaultPersonSettingsStatus = useStatus(getDefaultPersonSettings);
  const updateDefaultPersonSettingsStatus = useStatus(updateDefaultPersonSettings);
  const combinedStatus = combineStatuses(clientStatus.value, defaultPersonSettingsStatus);

  const initialState = {
    client: {
      ..._client,
      displayName: _client.displayName || 'Sift App',
      settings: {
        ..._client.settings,
        groupEmail: _get(_client, 'settings.groupEmail', defaultGroupEmailFormValue),
        groupMessage: _get(_client, 'settings.groupMessage', defaultGroupMessageFormValue),
        timezoneAndWorkHours: getDefaultTimezoneAndWorkHoursFormValue(_client.settings),
        orgChart: _get(_client, 'settings.orgChart', defaultOrgChartExportFormValue),
        assistants: _get(_client, 'settings.assistants', defaultAssistantFormValue),
        onboarding: _get(_client, 'settings.onboarding', defaultOnboardingFormValue),
      },
    },
    layouts: {
      'org-chart-highlight-by': _get(userConfig, '_layouts.org-chart-highlight-by', []),
      'org-chart-custom-attributes': _get(userConfig, '_layouts.org-chart-custom-attributes', []),
      'org-chart-sortable-attributes': _get(
        userConfig,
        '_layouts.org-chart-sortable-attributes',
        [],
      ),
      'org-levels': _get(userConfig, '_layouts.org-levels', []),
      filters: _get(userConfig, '_layouts.filters', []),
    },
    person: defaultPersonSettings,
  } as Types.FeatureValues;

  const combinedUpdateStatuses = combineStatuses(
    clientStatus.value,
    updateLayoutStatus,
    updateDefaultPersonSettingsStatus,
  );
  const rootNodes = useData(getRootNodes);
  const userStatus = useStatus(entityTypesFetch);
  const fields = _get(userConfig, '_fields') as FormValues.Fields;

  useEffect(() => {
    document.title = 'Feature Management';
    dispatch(getRootNodes());
  }, []);

  useEffect(() => {
    dispatch(getDefaultPersonSettings());
  }, []);

  useFormSnackbar({ status: combinedUpdateStatuses });

  const classes = useStyles();
  const history = useHistory();
  const tabPath = history.location.pathname.replace(/(\/admin\/feature-management\/?)/g, '');
  const tab = tabPath === '' ? 'directory' : tabPath;

  const handleTabChange = (e: any, value: string) => {
    let pathname = '/admin/feature-management';
    pathname = `${pathname}/${value}`;
    history.push({ pathname });
  };

  const handleSubmit = async (values: Types.FeatureValues) => {
    const _timezoneAndWorkHours = _get(values.client, 'settings.timezoneAndWorkHours');
    const timezoneAndWorkHoursSettingsToSave =
      !!_timezoneAndWorkHours && convertTimezoneAndWorkHoursValueForRequest(_timezoneAndWorkHours);

    const newValues =
      !!_timezoneAndWorkHours && !_isEqual(values.client, initialState.client)
        ? {
            ...values.client,
            settings: {
              ...values.client.settings,
              timezoneAndWorkHours: timezoneAndWorkHoursSettingsToSave,
            },
          }
        : !_isEqual(values.client, initialState.client)
        ? values.client
        : null;

    const layoutPayloadKeys = Object.keys(values.layouts).filter(
      key => !_isEqual(values.layouts[key], initialState.layouts[key]),
    );
    const layoutPayload = layoutPayloadKeys.map(key => ({
      entityField: PERSON_ENTITY_TYPE_KEY,
      layoutKey: key,
      children: values.layouts[key],
    }));

    newValues && dispatch(updateClient(newValues));
    layoutPayloadKeys.length && (await dispatch(updateLayoutFetch(layoutPayload)));

    if (!_isEqual(values.person, initialState.person)) {
      await dispatch(updateDefaultPersonSettings(values.person));
      dispatch(getDefaultPersonSettings());
    }
  };

  const handleValidate = (values: Types.FeatureValues) => {
    const errors = {} as Types.FeatureFormErrors;

    if (values.client.settings.timezoneAndWorkHours.defaultWorkHoursEnabled) {
      const workHoursByDayEntries = Object.entries(
        values.client.settings.timezoneAndWorkHours.workHoursByDay,
      );
      workHoursByDayEntries.forEach(([day, value]) => {
        const hoursEnabled = values.client.settings.timezoneAndWorkHours.hoursEnabled;
        const locationEnabled = values.client.settings.timezoneAndWorkHours.locationEnabled;
        const startTime = _get(value, 'startTime');
        const endTime = _get(value, 'endTime');
        const location = _get(value, 'location');

        if (Boolean(value)) {
          if (
            hoursEnabled &&
            Boolean(startTime) &&
            Boolean(endTime) &&
            !moment(startTime).isBefore(endTime)
          ) {
            // startTime may not be before end time
            errors.timezoneAndWorkHours = {
              ...errors.timezoneAndWorkHours,
              [day]: 'Start time must be before end time.',
            };
          } else if (
            (Boolean(hoursEnabled) && Boolean(startTime) && !Boolean(endTime)) ||
            (Boolean(hoursEnabled) && Boolean(endTime) && !Boolean(startTime))
          ) {
            // there must be both a start time or end time, or they must both be empty (if location is enabled)
            errors.timezoneAndWorkHours = {
              ...errors.timezoneAndWorkHours,
              [day]: 'Start time and end time must be defined, or must both be empty.',
            };
          } else if (
            (!Boolean(locationEnabled) && Boolean(hoursEnabled) && !Boolean(startTime)) ||
            (!Boolean(locationEnabled) && Boolean(hoursEnabled) && !Boolean(endTime))
          ) {
            // there must be a start and end time if location is disabled
            errors.timezoneAndWorkHours = {
              ...errors.timezoneAndWorkHours,
              [day]: 'Start time and end time must be defined.',
            };
          } else if (
            locationEnabled &&
            !Boolean(location) &&
            hoursEnabled &&
            !Boolean(startTime) &&
            !Boolean(endTime)
          ) {
            // there must be a location OR start time and end time if location is enabled
            errors.timezoneAndWorkHours = {
              ...errors.timezoneAndWorkHours,
              [day]: 'Start time and end time OR a location must be defined.',
            };
          } else if (!hoursEnabled && !Boolean(location)) {
            // there must be a location if work hours are not enabled
            errors.timezoneAndWorkHours = {
              ...errors.timezoneAndWorkHours,
              [day]: 'You must add a location for each working day.',
            };
          }
        }
      });
    }

    const limit = _get(values, 'settings.export.limit', 1000);
    if (isNaN(limit)) {
      errors.export = 'Limit must be a number';
    }
    if (limit < 1) {
      errors.export = 'Limit must be greater than 1';
    }
    if (limit > 1000) {
      errors.export = 'Limit cannot be greater than 1,000';
    }

    return errors;
  };

  return (
    <Page classes={{ root: classes.layoutRoot }}>
      <Breadcrumbs routes={['Home']} />
      <PageTitle
        title="Feature Management"
        subtitle="Turn different features inside of Sift on or off and change their settings."
      />
      <Loader status={combinedStatus}>
        <Formik
          initialValues={initialState}
          validate={handleValidate}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {props => {
            const { values, setFieldValue, dirty, resetForm } = props;
            const errors = props.errors as Types.FeatureFormErrors;

            return (
              <Form>
                <Tabs onChange={(e, value) => handleTabChange(e, value)} value={tab || 'directory'}>
                  <Tab label="Directory/Search" value={'directory'} />
                  <Tab label="profile" value="profile" />
                  <Tab label="Org Chart" value="org-chart" />
                  <Tab label="Other" value="other" />
                </Tabs>
                <div>
                  {tab === 'directory' && (
                    <Directory
                      setFieldValue={setFieldValue}
                      values={values}
                      errors={errors}
                      userStatus={userStatus}
                      fields={fields}
                    />
                  )}
                  {tab === 'profile' && (
                    <Profile setFieldValue={setFieldValue} values={values} errors={errors} />
                  )}
                  {tab === 'org-chart' && (
                    <OrgChart
                      setFieldValue={setFieldValue}
                      values={values}
                      rootNodes={_get(rootNodes, 'results[0].items', [])}
                      userStatus={userStatus}
                      fields={fields}
                    />
                  )}
                  {tab === 'other' && (
                    <Onboarding
                      value={values.client.settings.onboarding}
                      onChange={setFieldValue}
                    />
                  )}
                </div>
                <PageFormToolbar
                  dirty={dirty}
                  preContent={<PageFormErrorMessage errors={errors} />}
                >
                  <CancelButton onClick={resetForm} />
                  <SaveButton
                    onClick={() => {
                      props.handleSubmit();
                      resetForm({ values });
                    }}
                    disabled={!dirty || !_isEmpty(errors)}
                  />
                </PageFormToolbar>
                <Prompt
                  when={dirty}
                  message="Your work is not saved! Are you sure you want to leave?"
                />
                <HandleRedirect />
              </Form>
            );
          }}
        </Formik>
      </Loader>
    </Page>
  );
}
const useStyles = makeStyles(theme => {
  return {
    root: {},
    layoutRoot: {
      display: 'flex',
      flex: '1 1 auto',
    },
  };
});

export default Features;
